import { ref, readonly } from 'vue';
import { ENV_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import type { FeedbackRequest, FeedbackError } from '@/types/feedback';
import { createFeedbackError, isFeedbackErrorBody, mapServerError } from '@/types/feedback';

const isSubmitting = ref(false);
const feedbackError = ref<FeedbackError | null>(null);
const isSuccess = ref(false);

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = parseInt(header, 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

export function useFeedback() {
  async function submitFeedback(request: FeedbackRequest): Promise<boolean> {
    isSubmitting.value = true;
    feedbackError.value = null;
    isSuccess.value = false;

    try {
      const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
      });

      if (response.ok) {
        isSuccess.value = true;
        return true;
      }

      if (response.status === 401) {
        feedbackError.value = createFeedbackError(
          'unauthorized',
          STRINGS.feedback.errors.unauthorized
        );
        return false;
      }

      if (response.status === 403) {
        feedbackError.value = createFeedbackError(
          'email_not_verified',
          STRINGS.feedback.errors.emailNotVerified
        );
        return false;
      }

      if (response.status === 429) {
        const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
        feedbackError.value = createFeedbackError(
          'rate_limited',
          STRINGS.feedback.errors.rateLimited,
          retryAfter
        );
        return false;
      }

      if (response.status === 503) {
        feedbackError.value = createFeedbackError(
          'feature_disabled',
          STRINGS.feedback.errors.featureDisabled
        );
        return false;
      }

      // Try to parse error code from body
      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        // Non-JSON response body, fall through to generic error
      }

      if (isFeedbackErrorBody(body)) {
        const errorType = mapServerError(body.code);
        const message =
          body.message ??
          (errorType === 'github_error'
            ? STRINGS.feedback.errors.githubError
            : STRINGS.feedback.errors.unknown);
        feedbackError.value = createFeedbackError(errorType, message);
        return false;
      }

      feedbackError.value = createFeedbackError('unknown', STRINGS.feedback.errors.unknown);
      return false;
    } catch (e) {
      console.error('Feedback submission failed:', e);
      feedbackError.value = createFeedbackError('network_error', STRINGS.feedback.errors.network);
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  function reset(): void {
    isSubmitting.value = false;
    feedbackError.value = null;
    isSuccess.value = false;
  }

  return {
    isSubmitting: readonly(isSubmitting),
    error: readonly(feedbackError),
    isSuccess: readonly(isSuccess),
    submitFeedback,
    reset,
  };
}
